/**
 * SH101Panel - Interface pour le Roland SH-101
 * Design inspiré du vrai SH-101 avec son look gris/bleu caractéristique
 */

import { themeManager } from '../themes/ThemeManager.js';
import { WaveSelector } from '../components/WaveSelector.js';
import { PresetSelector } from '../components/PresetSelector.js';
import { SH101_PRESETS } from '../../state/InstrumentPresets.js';

export class SH101Panel {
    constructor(sh101, store) {
        this.synth = sh101;
        this.store = store;
        this.panelId = 'sh101';
        this.components = {
            knobs: {},
            selects: {},
            stepButtons: []
        };
    }

    isCollapsed() {
        return this.store?.isPanelCollapsed(this.panelId) || false;
    }

    render(container) {
        this.container = container;
        const isCollapsed = this.isCollapsed();
        const isPowered = this.store.getPowerState('sh101');

        this.synth.setPower(isPowered);

        container.innerHTML = `
            <div class="machine sh101 ${!isPowered ? 'powered-off' : ''} ${isCollapsed ? 'collapsed' : ''}" id="sh101_machine">
                <div class="machine-title sh101-title">
                    <button class="collapse-btn" id="sh101_collapse" title="Réduire/Agrandir">${isCollapsed ? '▶' : '▼'}</button>
                    ROLAND <span class="model">SH-101</span>
                    <span class="brand">Synthesizer</span>
                    <div id="sh101_presets"></div>
                    <button class="power-btn ${isPowered ? 'on' : ''}" id="sh101_power">⏻</button>
                </div>

                <div class="machine-content ${isCollapsed ? 'hidden' : ''}" id="sh101_content">
                <div class="sh101-controls">
                    <!-- VCO Section -->
                    <div class="sh-section vco-section">
                        <div class="section-title">VCO</div>
                        <div class="controls">
                            <div class="toggle-row">
                                <div class="toggle-group" id="sh_vco_saw"></div>
                                <div class="toggle-group" id="sh_vco_pulse"></div>
                            </div>
                            <div class="knob-group" id="sh_vco_pw"></div>
                            <div class="knob-group" id="sh_vco_oct"></div>
                        </div>
                    </div>

                    <!-- Source Mixer -->
                    <div class="sh-section mixer-section">
                        <div class="section-title">SOURCE MIX</div>
                        <div class="controls">
                            <div class="knob-group" id="sh_sub1"></div>
                            <div class="knob-group" id="sh_sub2"></div>
                            <div class="knob-group" id="sh_noise"></div>
                        </div>
                    </div>

                    <!-- VCF Section -->
                    <div class="sh-section vcf-section">
                        <div class="section-title">VCF</div>
                        <div class="controls">
                            <div class="knob-group" id="sh_cutoff"></div>
                            <div class="knob-group" id="sh_resonance"></div>
                            <div class="knob-group" id="sh_env_amt"></div>
                            <div class="knob-group" id="sh_keytrack"></div>
                        </div>
                    </div>

                    <!-- Envelope Section -->
                    <div class="sh-section env-section">
                        <div class="section-title">ENV</div>
                        <div class="controls">
                            <div class="knob-group" id="sh_env_a"></div>
                            <div class="knob-group" id="sh_env_d"></div>
                            <div class="knob-group" id="sh_env_s"></div>
                            <div class="knob-group" id="sh_env_r"></div>
                        </div>
                    </div>

                    <!-- LFO Section -->
                    <div class="sh-section lfo-section">
                        <div class="section-title">LFO</div>
                        <div class="controls">
                            <div id="sh_lfo_wave"></div>
                            <div class="knob-group" id="sh_lfo_rate"></div>
                            <div class="knob-group" id="sh_lfo_pitch"></div>
                            <div class="knob-group" id="sh_lfo_filter"></div>
                            <div class="knob-group" id="sh_lfo_pw"></div>
                        </div>
                    </div>

                    <!-- Master Section -->
                    <div class="sh-section master-section">
                        <div class="section-title">MASTER</div>
                        <div class="controls">
                            <div class="knob-group" id="sh_porta"></div>
                            <div class="knob-group" id="sh_volume"></div>
                        </div>
                    </div>
                </div>

                <!-- Sequencer -->
                <div class="sequencer machine-sequencer">
                    <div class="step-markers">
                        ${[...Array(16)].map((_, i) => `<span class="step-marker">${i + 1}</span>`).join('')}
                    </div>
                    <div class="seq-row">
                        <span class="seq-label">GATE</span>
                        <div class="steps" id="sh101_gates"></div>
                    </div>
                    <div class="seq-row">
                        <span class="seq-label">NOTE</span>
                        <div class="note-row" id="sh101_notes"></div>
                    </div>
                </div>
                </div><!-- /machine-content -->
            </div>
        `;

        this.setupCollapseButton();
        this.setupPowerButton();

        if (!isCollapsed) {
            this.createVcoControls();
            this.createMixerControls();
            this.createFilterControls();
            this.createEnvelopeControls();
            this.createLfoControls();
            this.createMasterControls();
            this.createSequencer();
            this.setupPresetSelector();
            this._loadParams();
        }
    }

    setupCollapseButton() {
        const collapseBtn = document.getElementById('sh101_collapse');
        if (!collapseBtn) return;

        collapseBtn.addEventListener('click', () => {
            this.store.togglePanelCollapsed(this.panelId);
            this.destroy();
            this.render(this.container);
        });
    }

    setupPowerButton() {
        const powerBtn = document.getElementById('sh101_power');
        const machine = document.getElementById('sh101_machine');

        powerBtn.addEventListener('click', () => {
            const newState = !this.synth.getPower();
            this.synth.setPower(newState);
            this.store.setPowerState('sh101', newState);
            powerBtn.classList.toggle('on', newState);
            machine.classList.toggle('powered-off', !newState);
        });
    }

    createVcoControls() {
        // SAW toggle
        const sawContainer = document.getElementById('sh_vco_saw');
        sawContainer.innerHTML = `
            <button class="wave-toggle ${this.synth._params.vcoSaw ? 'active' : ''}" id="sh_saw_btn">
                <span class="wave-icon">⩘</span>
                <span class="wave-label">SAW</span>
            </button>
        `;
        document.getElementById('sh_saw_btn').addEventListener('click', (e) => {
            const active = !e.target.closest('button').classList.contains('active');
            e.target.closest('button').classList.toggle('active', active);
            this.synth.setVcoSaw(active);
            this._saveParams();
        });

        // PULSE toggle
        const pulseContainer = document.getElementById('sh_vco_pulse');
        pulseContainer.innerHTML = `
            <button class="wave-toggle ${this.synth._params.vcoPulse ? 'active' : ''}" id="sh_pulse_btn">
                <span class="wave-icon">⊓</span>
                <span class="wave-label">PULSE</span>
            </button>
        `;
        document.getElementById('sh_pulse_btn').addEventListener('click', (e) => {
            const active = !e.target.closest('button').classList.contains('active');
            e.target.closest('button').classList.toggle('active', active);
            this.synth.setVcoPulse(active);
            this._saveParams();
        });

        // Pulse Width
        this.components.knobs.vcoPW = themeManager.createKnob(
            document.getElementById('sh_vco_pw'), {
                min: 0.1, max: 0.9, value: this.synth._params.vcoPulseWidth,
                label: 'PW', step: 0.01
            }
        ).onChange(value => {
            this.synth.setVcoPulseWidth(value);
            this._saveParams();
        });

        // Octave
        this.components.knobs.vcoOct = themeManager.createKnob(
            document.getElementById('sh_vco_oct'), {
                min: -2, max: 1, value: this.synth._params.vcoOctave,
                label: 'RANGE', step: 1
            }
        ).onChange(value => {
            this.synth.setVcoOctave(value);
            this._saveParams();
        });
    }

    createMixerControls() {
        this.components.knobs.sub1Level = themeManager.createKnob(
            document.getElementById('sh_sub1'), {
                min: 0, max: 1, value: this.synth._params.sub1Level,
                label: 'SUB 1', step: 0.01
            }
        ).onChange(value => {
            this.synth.setSub1Level(value);
            this._saveParams();
        });

        this.components.knobs.sub2Level = themeManager.createKnob(
            document.getElementById('sh_sub2'), {
                min: 0, max: 1, value: this.synth._params.sub2Level,
                label: 'SUB 2', step: 0.01
            }
        ).onChange(value => {
            this.synth.setSub2Level(value);
            this._saveParams();
        });

        this.components.knobs.noiseLevel = themeManager.createKnob(
            document.getElementById('sh_noise'), {
                min: 0, max: 1, value: this.synth._params.noiseLevel,
                label: 'NOISE', step: 0.01
            }
        ).onChange(value => {
            this.synth.setNoiseLevel(value);
            this._saveParams();
        });
    }

    createFilterControls() {
        this.components.knobs.cutoff = themeManager.createKnob(
            document.getElementById('sh_cutoff'), {
                min: 20, max: 20000, value: this.synth._params.lpfCutoff,
                label: 'FREQ', step: 1
            }
        ).onChange(value => {
            this.synth.setLpfCutoff(value);
            this._saveParams();
        });

        this.components.knobs.resonance = themeManager.createKnob(
            document.getElementById('sh_resonance'), {
                min: 0, max: 20, value: this.synth._params.lpfResonance,
                label: 'RES', step: 0.1
            }
        ).onChange(value => {
            this.synth.setLpfResonance(value);
            this._saveParams();
        });

        this.components.knobs.envAmt = themeManager.createKnob(
            document.getElementById('sh_env_amt'), {
                min: 0, max: 1, value: this.synth._params.lpfEnvAmount,
                label: 'ENV', step: 0.01
            }
        ).onChange(value => {
            this.synth.setLpfEnvAmount(value);
            this._saveParams();
        });

        this.components.knobs.keyTrack = themeManager.createKnob(
            document.getElementById('sh_keytrack'), {
                min: 0, max: 1, value: this.synth._params.lpfKeyTrack,
                label: 'KYBD', step: 0.01
            }
        ).onChange(value => {
            this.synth.setLpfKeyTrack(value);
            this._saveParams();
        });
    }

    createEnvelopeControls() {
        const envParams = [
            { id: 'sh_env_a', param: 'envAttack', label: 'A', setter: 'setEnvAttack' },
            { id: 'sh_env_d', param: 'envDecay', label: 'D', setter: 'setEnvDecay' },
            { id: 'sh_env_s', param: 'envSustain', label: 'S', setter: 'setEnvSustain' },
            { id: 'sh_env_r', param: 'envRelease', label: 'R', setter: 'setEnvRelease' }
        ];

        envParams.forEach(p => {
            const isTime = p.label !== 'S';
            this.components.knobs[p.param] = themeManager.createKnob(
                document.getElementById(p.id), {
                    min: isTime ? 0.001 : 0,
                    max: isTime ? 4 : 1,
                    value: this.synth._params[p.param],
                    label: p.label, step: 0.01
                }
            ).onChange(value => {
                this.synth[p.setter](value);
                this._saveParams();
            });
        });
    }

    createLfoControls() {
        // LFO Wave selector
        const lfoWaveContainer = document.getElementById('sh_lfo_wave');
        this.components.lfoWave = new WaveSelector(lfoWaveContainer, {
            waveforms: [
                { value: 'triangle', label: 'TRI' },
                { value: 'square', label: 'SQR' },
                { value: 'sawtooth', label: 'SAW' }
            ],
            value: this.synth._params.lfoWave
        }).onChange(wave => {
            this.synth.setLfoWave(wave);
            this._saveParams();
        });

        this.components.knobs.lfoRate = themeManager.createKnob(
            document.getElementById('sh_lfo_rate'), {
                min: 0.1, max: 30, value: this.synth._params.lfoRate,
                label: 'RATE', step: 0.1
            }
        ).onChange(value => {
            this.synth.setLfoRate(value);
            this._saveParams();
        });

        this.components.knobs.lfoToPitch = themeManager.createKnob(
            document.getElementById('sh_lfo_pitch'), {
                min: 0, max: 1, value: this.synth._params.lfoToPitch,
                label: 'VCO', step: 0.01
            }
        ).onChange(value => {
            this.synth.setLfoToPitch(value);
            this._saveParams();
        });

        this.components.knobs.lfoToFilter = themeManager.createKnob(
            document.getElementById('sh_lfo_filter'), {
                min: 0, max: 1, value: this.synth._params.lfoToFilter,
                label: 'VCF', step: 0.01
            }
        ).onChange(value => {
            this.synth.setLfoToFilter(value);
            this._saveParams();
        });

        this.components.knobs.lfoToPW = themeManager.createKnob(
            document.getElementById('sh_lfo_pw'), {
                min: 0, max: 1, value: this.synth._params.lfoToPW,
                label: 'PWM', step: 0.01
            }
        ).onChange(value => {
            this.synth.setLfoToPW(value);
            this._saveParams();
        });
    }

    createMasterControls() {
        this.components.knobs.portamento = themeManager.createKnob(
            document.getElementById('sh_porta'), {
                min: 0, max: 0.5, value: this.synth._params.portamento,
                label: 'PORTA', step: 0.01
            }
        ).onChange(value => {
            this.synth.setPortamento(value);
            this._saveParams();
        });

        this.components.knobs.volume = themeManager.createKnob(
            document.getElementById('sh_volume'), {
                min: 0, max: 1, value: this.synth._params.volume,
                label: 'VOLUME', step: 0.01
            }
        ).onChange(value => {
            this.synth.setVolume(value);
            this._saveParams();
        });
    }

    createSequencer() {
        const notesContainer = document.getElementById('sh101_notes');
        const gatesContainer = document.getElementById('sh101_gates');

        // Note selectors
        for (let i = 0; i < 16; i++) {
            this.components.selects[`note_${i}`] = themeManager.createSelect(notesContainer, {
                options: this.store.noteOptions,
                value: this.store.patterns.sh101Notes ? this.store.patterns.sh101Notes[i] : 'C3',
                className: 'note-select'
            }).onChange(value => {
                this.store.setStep('sh101Notes', i, value);
            });
        }

        // Gate buttons
        for (let i = 0; i < 16; i++) {
            const stepBtn = themeManager.createStepButton(gatesContainer, {
                index: i,
                type: 'gate',
                active: this.store.patterns.sh101Gates ? this.store.patterns.sh101Gates[i] : false
            }).onChange(active => {
                this.store.setStep('sh101Gates', i, active);
            });

            this.components.stepButtons.push(stepBtn);
        }
    }

    setupPresetSelector() {
        const container = document.getElementById('sh101_presets');
        if (!container) return;

        this.presetSelector = new PresetSelector({
            presets: SH101_PRESETS,
            currentPreset: 'Init',
            onChange: (name, data) => this._loadPreset(data)
        });

        container.appendChild(this.presetSelector.element);
    }

    _loadPreset(presetData) {
        if (!presetData) return;

        this.synth.setParams(presetData);
        this._updateControlsFromParams();
        this._saveParams();
    }

    _updateControlsFromParams() {
        const params = this.synth.getParams();

        // Update knobs
        Object.entries(this.components.knobs).forEach(([key, knob]) => {
            if (params[key] !== undefined) {
                knob.setValue(params[key]);
            }
        });

        // Update toggles
        const sawBtn = document.getElementById('sh_saw_btn');
        const pulseBtn = document.getElementById('sh_pulse_btn');
        if (sawBtn) sawBtn.classList.toggle('active', params.vcoSaw);
        if (pulseBtn) pulseBtn.classList.toggle('active', params.vcoPulse);

        // Update LFO wave
        if (this.components.lfoWave) {
            this.components.lfoWave.setValue(params.lfoWave);
        }
    }

    highlightStep(step) {
        this.components.stepButtons.forEach((btn, i) => {
            if (btn && btn.setPlaying) {
                btn.setPlaying(i === step);
            }
        });
    }

    _saveParams() {
        this.store?.setSynthParams('sh101', this.synth.getParams());
    }

    _loadParams() {
        const saved = this.store?.getSynthParams('sh101');
        if (saved) {
            this.synth.setParams(saved);
            this._updateControlsFromParams();
        }
    }

    destroy() {
        Object.values(this.components.knobs).forEach(k => k?.destroy?.());
        Object.values(this.components.selects).forEach(s => s?.destroy?.());
        this.components.stepButtons.forEach(b => b?.destroy?.());
        this.components.lfoWave?.destroy?.();
    }
}
